import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, max, min, required, validate } from '@angular/forms/signals';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { firstValueFrom } from 'rxjs';

import { OrdersConfigurationService } from '@core/services/orders-configuration';
import { UpdateOrdersConfigurationRequestV1 } from '@orders/configuration/orders-configuration';

interface OrdersConfigurationFormModel {
  cartReservationWindowMinutes: number;
  cartReservationLowTimeThresholdMinutes: number;
  cartReservationExtensionMinutes: number;
  cartReservationEnabled: boolean;
}

@Component({
  selector: 'yt-orders-configuration',
  standalone: true,
  imports: [FormsModule, CardModule, InputNumberModule, ButtonModule, ProgressSpinnerModule, ToggleSwitchModule],
  templateUrl: './configuration.html',
  styleUrl: './configuration.scss'
})
export class OrdersConfiguration {
  private readonly configService = inject(OrdersConfigurationService);
  private readonly messageService = inject(MessageService);

  readonly configResource = this.configService.createOrdersConfigurationResource();
  readonly saving = signal<boolean>(false);
  readonly isLoading = computed(() => this.configResource.isLoading());

  readonly formModel = signal<OrdersConfigurationFormModel>({
    cartReservationWindowMinutes: 20,
    cartReservationLowTimeThresholdMinutes: 2,
    cartReservationExtensionMinutes: 10,
    cartReservationEnabled: true
  });

  readonly configForm = form(this.formModel, (path) => {
    required(path.cartReservationWindowMinutes, { message: 'Window is required' });
    min(path.cartReservationWindowMinutes, 1, { message: 'Must be at least 1 minute' });
    max(path.cartReservationWindowMinutes, 1440, { message: 'Must be 1440 minutes (24h) or less' });
    validate(path.cartReservationWindowMinutes, ({ value }) =>
      Number.isInteger(value()) ? null : { kind: 'notInteger', message: 'Must be a whole number' }
    );

    required(path.cartReservationLowTimeThresholdMinutes, { message: 'Low-time threshold is required' });
    min(path.cartReservationLowTimeThresholdMinutes, 0, { message: 'Must be 0 or greater' });
    // Cross-field: the threshold cannot exceed the window. Reading the form model here re-runs this validator
    // whenever the window changes, mirroring the server's paired invariant.
    validate(path.cartReservationLowTimeThresholdMinutes, ({ value }) => {
      if (!Number.isInteger(value())) {
        return { kind: 'notInteger', message: 'Must be a whole number' };
      }
      return value() <= this.formModel().cartReservationWindowMinutes
        ? null
        : { kind: 'aboveWindow', message: 'Must be at most the window' };
    });

    required(path.cartReservationExtensionMinutes, { message: 'Extension is required' });
    min(path.cartReservationExtensionMinutes, 1, { message: 'Must be at least 1 minute' });
    // Cross-field: the extension cannot exceed the window, mirroring the server's paired invariant. Re-runs
    // whenever the window changes because it reads the form model here.
    validate(path.cartReservationExtensionMinutes, ({ value }) => {
      if (!Number.isInteger(value())) {
        return { kind: 'notInteger', message: 'Must be a whole number' };
      }
      return value() <= this.formModel().cartReservationWindowMinutes
        ? null
        : { kind: 'aboveWindow', message: 'Must be at most the window' };
    });
  });

  constructor() {
    effect(() => {
      const config = this.configResource.value();
      if (!config) {
        return;
      }
      untracked(() => {
        this.formModel.set({
          cartReservationWindowMinutes: config.cartReservationWindowMinutes,
          cartReservationLowTimeThresholdMinutes: config.cartReservationLowTimeThresholdMinutes,
          cartReservationExtensionMinutes: config.cartReservationExtensionMinutes,
          cartReservationEnabled: config.cartReservationEnabled
        });
      });
    });
  }

  async onSave(): Promise<void> {
    if (this.configForm().invalid()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation',
        detail: 'Please fix the errors before saving'
      });
      return;
    }

    this.saving.set(true);
    const model = this.formModel();
    const request: UpdateOrdersConfigurationRequestV1 = {
      cartReservationWindowMinutes: model.cartReservationWindowMinutes,
      cartReservationLowTimeThresholdMinutes: model.cartReservationLowTimeThresholdMinutes,
      cartReservationExtensionMinutes: model.cartReservationExtensionMinutes,
      cartReservationEnabled: model.cartReservationEnabled
    };

    try {
      await firstValueFrom(this.configService.updateOrdersConfiguration(request));
      this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Orders configuration updated' });
      this.configResource.reload();
    } catch (error) {
      console.error('[OrdersConfiguration] save failed', error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save orders configuration' });
    } finally {
      this.saving.set(false);
    }
  }
}
